import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type FormEvent,
} from "react";

import { DocumentUploadPanel } from "@/components/applications/DocumentUploadPanel";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { Spinner } from "@/components/ui/Spinner";
import { Textarea } from "@/components/ui/Textarea";
import { useToast } from "@/components/ui/Toast";
import { ApiError } from "@/lib/api/client";
import { scrollToFormError } from "@/lib/scroll-to-form-error";
import { usePdfPasswordPrompt } from "@/hooks/usePdfPasswordPrompt";
import {
  COMPANY_BUSINESS_BRIEF_MAX_LENGTH,
  emptyLendingLine,
  finalizePatPercentInput,
  finalizeVintageYearsInput,
  FINANCING_TYPE_OPTIONS,
  isSidFinancing,
  lendingLineIsEmpty,
  loanAmountLimitMessage,
  loanAmountRangeError,
  PAT_PERCENT_MAX,
  PAT_PERCENT_MIN,
  SALES_PLATFORM_OPTIONS,
  sanitizeLoanAmountInput,
  sanitizePatPercentInput,
  sanitizeVintageYearsInput,
  VAT_NUMBER_LENGTH,
  validateFinancingDetails,
  type FinancingFormValues,
  type LendingLineFormValues,
} from "@/lib/partner-validations";
import { updateFinancingDetails } from "@/services/applications";
import {
  buildChecklist,
  createDocumentMetadata,
  deleteApplicationDocumentFile,
  deleteDocumentContainer,
  DOC_KEY_ADDITIONAL_DOCUMENTS,
  DOC_KEY_AECB_CREDIT_REPORT,
  DOC_KEY_AUDITED_FINANCIALS,
  DOC_KEY_BANK_STATEMENTS,
  DOC_KEY_BUSINESS_CONTRACTS,
  DOC_KEY_BUYER_INVOICES,
  DOC_KEY_INVOICES,
  DOC_KEY_RECEIVABLES_AGEING,
  DOC_KEY_VAT_RETURNS,
  financialYearOfDocument,
  listApplicationDocuments,
  listDocumentMappings,
  summarizeFinancialYears,
  uploadApplicationDocument,
  type ApplicationDocument,
  type DocumentMapping,
} from "@/services/documents";
import type { ApplicationDetail, DocumentChecklistItem } from "@/types/application";

type FinancialDocsPanelProps = {
  application: ApplicationDetail;
  onChanged?: () => Promise<void> | void;
};

type FinancingForm = FinancingFormValues;

/** Partner-facing copy (third person). API mappings still carry merchant first-person help text. */
const PARTNER_FINANCIAL_DOC_COPY: Record<string, string> = {
  [DOC_KEY_BANK_STATEMENTS]:
    "Please ensure you add all the bank statements which are related to the business transactions for the last 12 months. This will help us quickly give the best possible offer. Statements are needed for all revenue and expense bank accounts associated with business. The PDF/Excel should be downloaded directly from the bank’s portal.",
  [DOC_KEY_INVOICES]:
    "Upload the last 12 month Payment Gateway or Marketplace Sales Data for the Merchant.",
  [DOC_KEY_AECB_CREDIT_REPORT]:
    "Please upload latest AECB report of company, all key promoters, and holding company (if available).",
  [DOC_KEY_AUDITED_FINANCIALS]:
    "Please upload the audited balance sheet, profit & loss statement, and cash flow statement for last 2 years.",
  [DOC_KEY_VAT_RETURNS]:
    "Please upload VAT return filings for the last 12 months.",
};

const AUDITED_FINANCIALS_DESCRIPTION =
  "Select a financial year and upload the audited balance sheet, profit & loss statement, and cash flow statement for that year.";

function metricString(value: string | number | null | undefined): string {
  return value != null && String(value).trim() !== "" ? String(value) : "";
}

function filesVatFromApplication(value: boolean | null | undefined): string {
  if (value === true) return "true";
  if (value === false) return "false";
  return "";
}

function lendingLinesFromApplication(
  lines: ApplicationDetail["existing_lending_lines"],
): LendingLineFormValues[] {
  if (!lines?.length) return [];
  return lines.map((line) => ({
    lender_name: line.lender_name || "",
    loan_amount: line.loan_amount != null ? String(line.loan_amount) : "",
    facility_type: line.facility_type || "",
    outstanding: line.outstanding != null ? String(line.outstanding) : "",
    emi: line.emi != null ? String(line.emi) : "",
    remarks: line.remarks || "",
  }));
}

function amountInputString(value: string | number | null | undefined): string {
  if (value == null || String(value).trim() === "") return "";
  const amount = Number(value);
  if (!Number.isFinite(amount) || amount === 0) return "";
  return String(Math.trunc(amount));
}

function optionalAmount(raw: string): number | null {
  const trimmed = raw.trim();
  return trimmed ? Number(trimmed) : null;
}

function checklistItemComplete(item: DocumentChecklistItem | undefined): boolean {
  if (!item) return true;
  return item.uploaded || (item.files && item.files.length > 0);
}

function metaString(meta: Record<string, unknown> | null | undefined, key: string): string {
  const value = meta?.[key];
  return typeof value === "string" ? value : value != null ? String(value) : "";
}

function hasValidBank(meta: Record<string, unknown> | null | undefined): boolean {
  return Boolean(metaString(meta, "bank_name").trim() && metaString(meta, "account_number").trim());
}

function hasValidMarketplace(meta: Record<string, unknown> | null | undefined): boolean {
  return Boolean(metaString(meta, "marketplace").trim() && metaString(meta, "country").trim());
}

function hasValidBuyer(meta: Record<string, unknown> | null | undefined): boolean {
  return Boolean(metaString(meta, "buyer_name").trim());
}

function financingFromApplication(application: ApplicationDetail): FinancingForm {
  return {
    type_of_financing: application.type_of_financing || "",
    platform: application.platform || "",
    platform_name: application.platform_name || "",
    loan_amount_requested: amountInputString(application.loan_amount_requested),
    company_business_brief: application.company_business_brief || "",
    last_12_month_revenue: metricString(application.last_12_month_revenue),
    pat_percent: metricString(application.pat_percent),
    business_vintage_years: metricString(application.business_vintage_years),
    files_vat: filesVatFromApplication(application.files_vat),
    vat_number: application.vat_number || "",
    no_existing_loans: application.no_existing_loans === true,
    existing_lending_lines: lendingLinesFromApplication(
      application.existing_lending_lines,
    ),
  };
}

function hasFinancingData(application: ApplicationDetail): boolean {
  return Boolean(
    application.type_of_financing ||
    application.platform ||
    application.platform_name?.trim() ||
    (application.loan_amount_requested != null &&
      Number(application.loan_amount_requested) > 0) ||
    application.company_business_brief?.trim() ||
    metricString(application.last_12_month_revenue) ||
    metricString(application.pat_percent) ||
    metricString(application.business_vintage_years) ||
    application.files_vat === true ||
    application.files_vat === false ||
    application.vat_number?.trim() ||
    application.no_existing_loans === true ||
    (application.existing_lending_lines &&
      application.existing_lending_lines.length > 0),
  );
}

const FINANCING_TYPE_LABELS: Record<string, string> = Object.fromEntries(
  FINANCING_TYPE_OPTIONS.map((o) => [o.value, o.label]),
);

const PLATFORM_LABELS: Record<string, string> = Object.fromEntries(
  SALES_PLATFORM_OPTIONS.map((o) => [o.value, o.label]),
);

function InfoCard({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-xs font-medium uppercase tracking-wide text-muted">{label}</div>
      <p className="mt-1 whitespace-pre-wrap text-sm text-ink">{value || "—"}</p>
    </div>
  );
}

export function FinancialDocsPanel({ application, onChanged }: FinancialDocsPanelProps) {
  const toast = useToast();
  const { uploadFileWithPasswordPrompt, renderPasswordModal } = usePdfPasswordPrompt();
  const locked = application.financial_docs_editable === false;
  const [mappings, setMappings] = useState<DocumentMapping[]>([]);
  const [documents, setDocuments] = useState<ApplicationDocument[]>([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [docsRefreshing, setDocsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);

  const [financing, setFinancing] = useState<FinancingForm>(() =>
    financingFromApplication(application),
  );
  const [financingSaving, setFinancingSaving] = useState(false);
  const [financingError, setFinancingError] = useState<string | null>(null);
  const [financingFieldErrors, setFinancingFieldErrors] = useState<
    Record<string, string>
  >({});
  const [bankFieldErrors, setBankFieldErrors] = useState<Record<string, string>>({});
  const [marketFieldErrors, setMarketFieldErrors] = useState<Record<string, string>>(
    {},
  );
  const [buyerFieldErrors, setBuyerFieldErrors] = useState<Record<string, string>>({});

  const [showBankForm, setShowBankForm] = useState(false);
  const [bankName, setBankName] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [bankSaving, setBankSaving] = useState(false);

  const [showMarketForm, setShowMarketForm] = useState(false);
  const [marketplace, setMarketplace] = useState("");
  const [country, setCountry] = useState("");
  const [marketSaving, setMarketSaving] = useState(false);

  const [showBuyerForm, setShowBuyerForm] = useState(false);
  const [buyerName, setBuyerName] = useState("");
  const [buyerSaving, setBuyerSaving] = useState(false);

  const [showFinancialYearForm, setShowFinancialYearForm] = useState(false);
  const [financialYear, setFinancialYear] = useState("");
  const [financialYearError, setFinancialYearError] = useState<string | null>(null);
  const [financialYearSaving, setFinancialYearSaving] = useState(false);

  const [uploadingUuid, setUploadingUuid] = useState<string | null>(null);
  const [deletingKey, setDeletingKey] = useState<string | null>(null);
  const [activeUploadUuid, setActiveUploadUuid] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const refreshDocuments = useCallback(async () => {
    setDocsRefreshing(true);
    setError(null);
    try {
      const docs = await listApplicationDocuments(application.uuid);
      setDocuments(docs.filter((d) => !d.shareholder));
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : "Unable to refresh documents",
      );
    } finally {
      setDocsRefreshing(false);
    }
  }, [application.uuid]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setInitialLoading(true);
      setError(null);
      try {
        const [docMappings, docs] = await Promise.all([
          listDocumentMappings({ category: "financial_docs" }),
          listApplicationDocuments(application.uuid),
        ]);
        if (cancelled) return;
        setMappings(docMappings);
        setDocuments(docs.filter((d) => !d.shareholder));
      } catch (err) {
        if (cancelled) return;
        setError(
          err instanceof ApiError ? err.message : "Unable to load financial documents",
        );
      } finally {
        if (!cancelled) setInitialLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [application.uuid]);

  const bankMapping = useMemo(
    () => mappings.find((m) => m.document_type_key === DOC_KEY_BANK_STATEMENTS) || null,
    [mappings],
  );
  const invoiceMapping = useMemo(
    () => mappings.find((m) => m.document_type_key === DOC_KEY_INVOICES) || null,
    [mappings],
  );
  const auditedFinancialsMapping = useMemo(
    () => mappings.find((m) => m.document_type_key === DOC_KEY_AUDITED_FINANCIALS) || null,
    [mappings],
  );
  const receivablesAgeingMapping = useMemo(
    () => mappings.find((m) => m.document_type_key === DOC_KEY_RECEIVABLES_AGEING) || null,
    [mappings],
  );
  const businessContractsMapping = useMemo(
    () => mappings.find((m) => m.document_type_key === DOC_KEY_BUSINESS_CONTRACTS) || null,
    [mappings],
  );
  const buyerInvoicesMapping = useMemo(
    () => mappings.find((m) => m.document_type_key === DOC_KEY_BUYER_INVOICES) || null,
    [mappings],
  );

  const bankDocs = useMemo(
    () =>
      documents.filter(
        (d) => d.document_key === DOC_KEY_BANK_STATEMENTS && hasValidBank(d.metadata),
      ),
    [documents],
  );
  const marketplaceDocs = useMemo(
    () =>
      documents.filter(
        (d) => d.document_key === DOC_KEY_INVOICES && hasValidMarketplace(d.metadata),
      ),
    [documents],
  );
  const buyerInvoiceDocs = useMemo(
    () =>
      documents.filter(
        (d) => d.document_key === DOC_KEY_BUYER_INVOICES && hasValidBuyer(d.metadata),
      ),
    [documents],
  );
  const auditedFinancialsDocs = useMemo(
    () =>
      documents.filter(
        (d) => d.document_key === DOC_KEY_AUDITED_FINANCIALS && financialYearOfDocument(d),
      ),
    [documents],
  );

  const receivablesAgeingMandatoryAbove =
    receivablesAgeingMapping?.mandatory_above_loan_amount ?? null;
  const receivablesAgeingRequired =
    receivablesAgeingMandatoryAbove != null &&
    Number(financing.loan_amount_requested) > receivablesAgeingMandatoryAbove;
  const businessContractsMandatoryAbove =
    businessContractsMapping?.mandatory_above_loan_amount ?? null;
  const businessContractsRequired =
    businessContractsMandatoryAbove != null &&
    Number(financing.loan_amount_requested) > businessContractsMandatoryAbove;
  const buyerInvoicesMandatoryFinancing = buyerInvoicesMapping?.mandatory_when_financing ?? null;
  const buyerInvoicesRequired =
    buyerInvoicesMandatoryFinancing != null &&
    financing.type_of_financing === buyerInvoicesMandatoryFinancing;

  const flatChecklist: DocumentChecklistItem[] = useMemo(() => {
    const flatMappings = mappings.filter(
      (m) =>
        m.document_type_key !== DOC_KEY_BANK_STATEMENTS &&
        m.document_type_key !== DOC_KEY_INVOICES &&
        m.document_type_key !== DOC_KEY_ADDITIONAL_DOCUMENTS &&
        m.document_type_key !== DOC_KEY_AUDITED_FINANCIALS &&
        m.document_type_key !== DOC_KEY_BUYER_INVOICES,
    );
    const flatDocs = documents.filter(
      (d) =>
        d.document_key !== DOC_KEY_BANK_STATEMENTS &&
        d.document_key !== DOC_KEY_INVOICES &&
        d.document_key !== DOC_KEY_ADDITIONAL_DOCUMENTS &&
        d.document_key !== DOC_KEY_AUDITED_FINANCIALS &&
        d.document_key !== DOC_KEY_BUYER_INVOICES,
    );
    const vatRequired = financing.files_vat === "true";
    return buildChecklist(flatMappings, flatDocs).map((item) => {
      // Default to the mapping's static is_mandatory (buildChecklist already set
      // `required` from it); only override for documents whose mandatory status
      // depends on another field, so it stays reactive to that field's value.
      let required = item.required;
      if (item.document_key === DOC_KEY_AECB_CREDIT_REPORT) {
        required = true;
      } else if (item.document_key === DOC_KEY_VAT_RETURNS) {
        required = vatRequired;
      } else if (item.document_key === DOC_KEY_RECEIVABLES_AGEING) {
        required = receivablesAgeingRequired;
      } else if (item.document_key === DOC_KEY_BUSINESS_CONTRACTS) {
        required = businessContractsRequired;
      }
      return {
        ...item,
        description: PARTNER_FINANCIAL_DOC_COPY[item.document_key] ?? item.description,
        required,
        multi_upload: true,
      };
    });
  }, [mappings, documents, financing.files_vat, receivablesAgeingRequired, businessContractsRequired]);

  const salesPlatformRequired = isSidFinancing(financing.type_of_financing);
  const platformNameRequired = financing.platform === "other";
  const bankStatementsComplete = useMemo(() => {
    return bankDocs.some((d) => (d.document_files || []).length > 0);
  }, [bankDocs]);
  const aecbComplete = useMemo(() => {
    const item = flatChecklist.find(
      (i) => i.document_key === DOC_KEY_AECB_CREDIT_REPORT,
    );
    return checklistItemComplete(item);
  }, [flatChecklist]);
  const minFinancialYears = auditedFinancialsMapping?.minimum_financial_years ?? 2;
  const financialsMandatoryAbove = auditedFinancialsMapping?.mandatory_above_loan_amount ?? null;
  const financialsRequired =
    financialsMandatoryAbove != null &&
    Number(financing.loan_amount_requested) > financialsMandatoryAbove;
  const financialYearsSummary = useMemo(
    () => summarizeFinancialYears(auditedFinancialsDocs),
    [auditedFinancialsDocs],
  );
  const financialsComplete =
    !financialsRequired || financialYearsSummary.uploaded.length >= minFinancialYears;
  const availableFinancialYears = (auditedFinancialsMapping?.financial_year_options ?? [])
    .filter((year) => !financialYearsSummary.selected.includes(year))
    .sort((a, b) => b.localeCompare(a, undefined, { numeric: true }));
  const vatRequired = financing.files_vat === "true";
  const vatComplete = useMemo(() => {
    if (!vatRequired) return true;
    const item = flatChecklist.find((i) => i.document_key === DOC_KEY_VAT_RETURNS);
    return checklistItemComplete(item);
  }, [flatChecklist, vatRequired]);
  const receivablesAgeingComplete = useMemo(() => {
    if (!receivablesAgeingRequired) return true;
    const item = flatChecklist.find((i) => i.document_key === DOC_KEY_RECEIVABLES_AGEING);
    return checklistItemComplete(item);
  }, [flatChecklist, receivablesAgeingRequired]);
  const businessContractsComplete = useMemo(() => {
    if (!businessContractsRequired) return true;
    const item = flatChecklist.find((i) => i.document_key === DOC_KEY_BUSINESS_CONTRACTS);
    return checklistItemComplete(item);
  }, [flatChecklist, businessContractsRequired]);
  const buyerInvoicesComplete = useMemo(() => {
    if (!buyerInvoicesRequired) return true;
    return buyerInvoiceDocs.some((d) => (d.document_files || []).length > 0);
  }, [buyerInvoiceDocs, buyerInvoicesRequired]);
  const otherDocsIncomplete = [
    !aecbComplete ? "Incomplete — upload at least one AECB credit report." : null,
    financialsRequired && !financialsComplete
      ? `Incomplete — audited financials are required for at least ${minFinancialYears} financial years when the requested amount is over ${financialsMandatoryAbove?.toLocaleString()} AED.`
      : null,
    vatRequired && !vatComplete
      ? "Incomplete — upload VAT returns because this merchant files VAT."
      : null,
    receivablesAgeingRequired && !receivablesAgeingComplete
      ? `Incomplete — upload Account Receivables Ageing because the requested amount is over ${receivablesAgeingMandatoryAbove?.toLocaleString()} AED.`
      : null,
    businessContractsRequired && !businessContractsComplete
      ? `Incomplete — upload Key Business Contracts and Agreement Copies because the requested amount is over ${businessContractsMandatoryAbove?.toLocaleString()} AED.`
      : null,
    buyerInvoicesRequired && !buyerInvoicesComplete
      ? "Incomplete — upload Invoice Copies of Customers because this is required for Invoice Discounting."
      : null,
  ].filter((message): message is string => Boolean(message));

  const hasOtherDocs = useMemo(
    () =>
      flatChecklist.some(
        (item) => item.uploaded || (item.files && item.files.length > 0),
      ),
    [flatChecklist],
  );
  const hasSectionData =
    hasFinancingData(application) ||
    bankDocs.length > 0 ||
    marketplaceDocs.length > 0 ||
    auditedFinancialsDocs.length > 0 ||
    hasOtherDocs;
  const isEditing = !locked && (editing || !hasSectionData);
  const docsReadOnly = !isEditing;
  const otherDocsDescription = locked
    ? "Documents are locked because this loan is verified."
    : docsReadOnly
      ? undefined
      : [
        "AECB Credit Report is required.",
        vatRequired
          ? "VAT Returns are required because this merchant files VAT."
          : "VAT Returns are required if the merchant files VAT.",
      ].join(" ");

  useEffect(() => {
    if (locked) {
      setEditing(false);
      return;
    }
    // Stay in edit once the empty form is shown, so the first save doesn't flip to view.
    if (!hasSectionData) setEditing(true);
  }, [locked, hasSectionData]);

  useEffect(() => {
    if (!isEditing) {
      setFinancing(financingFromApplication(application));
      setFinancingFieldErrors({});
      setFinancingError(null);
    }
  }, [application, isEditing]);

  const exitEditing = () => {
    setEditing(false);
    setShowBankForm(false);
    setShowMarketForm(false);
    setShowFinancialYearForm(false);
    setBankFieldErrors({});
    setMarketFieldErrors({});
    setFinancialYear("");
    setFinancialYearError(null);
    setFinancing(financingFromApplication(application));
    setFinancingFieldErrors({});
    setFinancingError(null);
    setError(null);
  };

  const clearLendingLineErrors = () => {
    setFinancingFieldErrors((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((key) => {
        if (key.startsWith("lending_line_") || key === "existing_lending_lines") {
          delete next[key];
        }
      });
      return next;
    });
  };

  const onSaveFinancing = async (event: FormEvent) => {
    event.preventDefault();
    if (!isEditing) return;
    const normalized: FinancingForm = {
      ...financing,
      pat_percent: finalizePatPercentInput(financing.pat_percent),
      business_vintage_years: finalizeVintageYearsInput(
        financing.business_vintage_years,
      ),
    };
    setFinancing(normalized);
    const fieldErrors = validateFinancingDetails(normalized);
    setFinancingFieldErrors(fieldErrors);
    if (Object.keys(fieldErrors).length) {
      setFinancingError("Please fix the highlighted financing fields.");
      scrollToFormError();
      return;
    }
    if (!bankStatementsComplete) {
      const message =
        "Upload at least one bank statement before saving. Add a bank account and upload a statement file.";
      setError(message);
      toast.error(message);
      scrollToFormError();
      return;
    }
    if (!aecbComplete) {
      const message = "Upload at least one AECB credit report before saving.";
      setError(message);
      toast.error(message);
      scrollToFormError();
      return;
    }
    if (!financialsComplete) {
      const message = `Upload audited financials for at least ${minFinancialYears} financial years before saving. Required when the requested amount is over ${financialsMandatoryAbove?.toLocaleString()} AED.`;
      setError(message);
      toast.error(message);
      scrollToFormError();
      return;
    }
    if (!vatComplete) {
      const message =
        "Upload VAT returns before saving. Required because this merchant files VAT.";
      setError(message);
      toast.error(message);
      scrollToFormError();
      return;
    }
    if (!receivablesAgeingComplete) {
      const message = `Upload Account Receivables Ageing before saving. Required when the requested amount is over ${receivablesAgeingMandatoryAbove?.toLocaleString()} AED.`;
      setError(message);
      toast.error(message);
      return;
    }
    if (!businessContractsComplete) {
      const message = `Upload Key Business Contracts and Agreement Copies before saving. Required when the requested amount is over ${businessContractsMandatoryAbove?.toLocaleString()} AED.`;
      setError(message);
      toast.error(message);
      return;
    }
    if (!buyerInvoicesComplete) {
      const message =
        "Upload Invoice Copies of Customers before saving. Required for Invoice Discounting.";
      setError(message);
      toast.error(message);
      return;
    }
    setFinancingSaving(true);
    setFinancingError(null);
    setError(null);
    try {
      const updated = await updateFinancingDetails(application.uuid, {
        type_of_financing: normalized.type_of_financing || undefined,
        platform: normalized.platform || "",
        platform_name:
          normalized.platform === "other" ? normalized.platform_name.trim() : "",
        loan_amount_requested: normalized.loan_amount_requested.trim() || null,
        company_business_brief: normalized.company_business_brief.trim(),
        last_12_month_revenue: optionalAmount(normalized.last_12_month_revenue),
        pat_percent: normalized.pat_percent.trim() || null,
        business_vintage_years: normalized.business_vintage_years.trim() || null,
        files_vat: normalized.files_vat === "true",
        vat_number:
          normalized.files_vat === "true" ? normalized.vat_number.trim() : null,
        no_existing_loans: normalized.no_existing_loans,
        existing_lending_lines: (normalized.no_existing_loans
          ? []
          : normalized.existing_lending_lines
        )
          .filter((line) => !lendingLineIsEmpty(line))
          .map((line) => ({
            lender_name: line.lender_name.trim(),
            loan_amount: Number(line.loan_amount),
            facility_type: line.facility_type.trim(),
            outstanding: optionalAmount(line.outstanding),
            emi: optionalAmount(line.emi),
            remarks: line.remarks.trim(),
          })),
      });
      setFinancing(financingFromApplication(updated));
      setShowBankForm(false);
      setShowMarketForm(false);
      setShowFinancialYearForm(false);
      setEditing(false);
      toast.success("Financing details saved.");
      await onChanged?.();
    } catch (err) {
      const message =
        err instanceof ApiError ? err.message : "Unable to save financing details";
      setFinancingError(message);
      toast.error(message);
      scrollToFormError();
    } finally {
      setFinancingSaving(false);
    }
  };

  const onAddBank = async () => {
    if (!isEditing) return;
    if (!bankMapping) {
      setError("Bank statements document type is not configured.");
      return;
    }
    const nextErrors: Record<string, string> = {};
    if (!bankName.trim()) nextErrors.bank_name = "Bank name is required.";
    if (!accountNumber.trim()) nextErrors.account_number = "Account number is required.";
    setBankFieldErrors(nextErrors);
    if (Object.keys(nextErrors).length) {
      setError("Bank name and account number are both required.");
      return;
    }
    setBankSaving(true);
    setError(null);
    try {
      await createDocumentMetadata(application.uuid, {
        documentTypeId: bankMapping.document_type,
        metadata: {
          bank_name: bankName.trim(),
          account_number: accountNumber.trim(),
        },
      });
      setBankName("");
      setAccountNumber("");
      setBankFieldErrors({});
      setShowBankForm(false);
      setEditing(true);
      await refreshDocuments();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Unable to add bank account");
    } finally {
      setBankSaving(false);
    }
  };

  const onAddMarketplace = async () => {
    if (!isEditing) return;
    if (!invoiceMapping) {
      setError("Sales data document type is not configured.");
      return;
    }
    const nextErrors: Record<string, string> = {};
    if (!marketplace.trim()) nextErrors.marketplace = "Marketplace name is required.";
    if (!country.trim()) nextErrors.country = "Country is required.";
    setMarketFieldErrors(nextErrors);
    if (Object.keys(nextErrors).length) {
      setError("Marketplace name and country are both required.");
      return;
    }
    setMarketSaving(true);
    setError(null);
    try {
      await createDocumentMetadata(application.uuid, {
        documentTypeId: invoiceMapping.document_type,
        metadata: {
          marketplace: marketplace.trim(),
          country: country.trim(),
        },
      });
      setMarketplace("");
      setCountry("");
      setMarketFieldErrors({});
      setShowMarketForm(false);
      setEditing(true);
      await refreshDocuments();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Unable to add marketplace");
    } finally {
      setMarketSaving(false);
    }
  };

  const onAddBuyer = async () => {
    if (!isEditing) return;
    if (!buyerInvoicesMapping) {
      setError("Invoice copies document type is not configured.");
      return;
    }
    const nextErrors: Record<string, string> = {};
    if (!buyerName.trim()) nextErrors.buyer_name = "Buyer name is required.";
    setBuyerFieldErrors(nextErrors);
    if (Object.keys(nextErrors).length) {
      setError("Buyer name is required.");
      return;
    }
    setBuyerSaving(true);
    setError(null);
    try {
      await createDocumentMetadata(application.uuid, {
        documentTypeId: buyerInvoicesMapping.document_type,
        metadata: {
          buyer_name: buyerName.trim(),
        },
      });
      setBuyerName("");
      setBuyerFieldErrors({});
      setShowBuyerForm(false);
      setEditing(true);
      await refreshDocuments();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Unable to add buyer");
    } finally {
      setBuyerSaving(false);
    }
  };

  const onAddFinancialYear = async () => {
    if (!isEditing) return;
    if (!auditedFinancialsMapping) {
      setError("Audited financials document type is not configured.");
      return;
    }
    if (!financialYear) {
      setFinancialYearError("Select a financial year.");
      return;
    }
    setFinancialYearError(null);
    setFinancialYearSaving(true);
    setError(null);
    try {
      await createDocumentMetadata(application.uuid, {
        documentTypeId: auditedFinancialsMapping.document_type,
        metadata: { financial_year: financialYear },
      });
      setFinancialYear("");
      setShowFinancialYearForm(false);
      setEditing(true);
      await refreshDocuments();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Unable to add financial year");
    } finally {
      setFinancialYearSaving(false);
    }
  };

  const openUpload = (documentUuid: string) => {
    if (!isEditing) return;
    setActiveUploadUuid(documentUuid);
    setError(null);
    fileInputRef.current?.click();
  };

  const onFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);
    const documentUuid = activeUploadUuid;
    event.target.value = "";
    setActiveUploadUuid(null);
    if (!isEditing || !files.length || !documentUuid) return;

    const doc = documents.find((d) => d.uuid === documentUuid);
    if (!doc) return;

    setUploadingUuid(documentUuid);
    setError(null);
    try {
      for (const file of files) {
        await uploadFileWithPasswordPrompt(file, (pdfPassword) =>
          uploadApplicationDocument(application.uuid, {
            documentTypeId: doc.document_type,
            file,
            documentUuid,
            pdfPassword,
          }),
        );
      }
      await refreshDocuments();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Upload failed");
    } finally {
      setUploadingUuid(null);
    }
  };

  const onDeleteFile = async (documentUuid: string, fileUuid: string) => {
    if (!isEditing) return;
    setDeletingKey(fileUuid);
    setError(null);
    try {
      await deleteApplicationDocumentFile(application.uuid, {
        documentUuid,
        documentFileUuid: fileUuid,
      });
      await refreshDocuments();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Unable to delete file");
    } finally {
      setDeletingKey(null);
    }
  };

  const onDeleteContainer = async (documentUuid: string, label: string) => {
    if (!isEditing) return;
    if (!window.confirm(`Delete ${label}? All uploaded files for it will be removed.`)) {
      return;
    }
    setDeletingKey(documentUuid);
    setError(null);
    try {
      await deleteDocumentContainer(application.uuid, documentUuid);
      await refreshDocuments();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Unable to delete");
    } finally {
      setDeletingKey(null);
    }
  };

  if (initialLoading) {
    return (
      <div className="flex justify-center py-16">
        <Spinner className="size-7" />
      </div>
    );
  }

  return (
    <>
      {renderPasswordModal()}
      <div className="space-y-5">
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          accept=".pdf,.jpg,.jpeg,.png,.webp"
          multiple
          onChange={(e) => void onFileChange(e)}
        />

        {error ? <Alert>{error}</Alert> : null}
        {locked ? (
          <Alert tone="success">
            This loan is verified. Financial details and documents are read-only.
          </Alert>
        ) : null}
        {docsRefreshing ? (
          <p className="text-xs text-muted">Updating documents…</p>
        ) : null}

        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-ink">Financial documents</h2>
            <p className="mt-1 text-sm text-muted">
              {locked
                ? "Locked because this loan is verified."
                : isEditing
                  ? "Edit financing details and manage document uploads."
                  : "Click Edit to make changes."}
            </p>
          </div>
          {!locked && hasSectionData ? (
            isEditing ? (
              <></>
            ) : (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  setFinancing(financingFromApplication(application));
                  setFinancingFieldErrors({});
                  setFinancingError(null);
                  setEditing(true);
                }}
              >
                Edit
              </Button>
            )
          ) : null}
        </div>

        <section className="rounded-2xl border border-line bg-surface-raised p-5 sm:p-6">
          <div className="mb-5">
            <h2 className="text-lg font-semibold text-ink">Financing details</h2>
            {locked || isEditing ? (
              <p className="mt-1 text-sm text-muted">
                {locked
                  ? "Financing details and documents are locked because this loan is verified."
                  : "Enter financing details and upload supporting documents, then save."}
              </p>
            ) : null}
          </div>
          {financingError ? <Alert className="mb-4">{financingError}</Alert> : null}

          {isEditing ? (
            <form onSubmit={(e) => void onSaveFinancing(e)} className="space-y-8">
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                <Select
                  label="Type of financing *"
                  name="type_of_financing"
                  value={financing.type_of_financing}
                  error={financingFieldErrors.type_of_financing}
                  onChange={(e) => {
                    const nextType = e.target.value;
                    setFinancing((prev) => ({ ...prev, type_of_financing: nextType }));
                    setFinancingFieldErrors((prev) => {
                      const next = { ...prev };
                      delete next.type_of_financing;
                      delete next.platform;
                      delete next.platform_name;
                      return next;
                    });
                  }}
                  options={[
                    { value: "", label: "Select financing type" },
                    ...FINANCING_TYPE_OPTIONS.map((o) => ({
                      value: o.value,
                      label: o.label,
                    })),
                  ]}
                />
                <Select
                  label={salesPlatformRequired ? "Sales platform *" : "Sales platform"}
                  name="platform"
                  value={financing.platform}
                  error={financingFieldErrors.platform}
                  onChange={(e) => {
                    setFinancing((prev) => ({ ...prev, platform: e.target.value }));
                    setFinancingFieldErrors((prev) => {
                      const next = { ...prev };
                      delete next.platform;
                      delete next.platform_name;
                      return next;
                    });
                  }}
                  options={[
                    { value: "", label: "Select sales platform" },
                    ...SALES_PLATFORM_OPTIONS.map((o) => ({
                      value: o.value,
                      label: o.label,
                    })),
                  ]}
                />
                <Input
                  label="Loan amount requested (AED) *"
                  name="loan_amount_requested"
                  inputMode="numeric"
                  placeholder="Enter amount"
                  value={financing.loan_amount_requested}
                  error={financingFieldErrors.loan_amount_requested}
                  hint={loanAmountLimitMessage()}
                  onChange={(e) => {
                    setFinancing((prev) => ({
                      ...prev,
                      loan_amount_requested: sanitizeLoanAmountInput(
                        e.target.value,
                        prev.loan_amount_requested,
                      ),
                    }));
                    setFinancingFieldErrors((prev) => {
                      const next = { ...prev };
                      delete next.loan_amount_requested;
                      return next;
                    });
                  }}
                  onBlur={(e) => {
                    const value = e.target.value.trim();
                    if (!value) return;
                    const amountError = loanAmountRangeError(value);
                    if (amountError) {
                      setFinancingFieldErrors((prev) => ({
                        ...prev,
                        loan_amount_requested: amountError,
                      }));
                    }
                  }}
                />
                <Input
                  label="Last 12 month revenue (AED) *"
                  name="last_12_month_revenue"
                  inputMode="numeric"
                  value={financing.last_12_month_revenue}
                  error={financingFieldErrors.last_12_month_revenue}
                  onChange={(e) => {
                    setFinancing((prev) => ({
                      ...prev,
                      last_12_month_revenue: e.target.value.replace(/[^0-9]/g, ""),
                    }));
                    setFinancingFieldErrors((prev) => {
                      const next = { ...prev };
                      delete next.last_12_month_revenue;
                      return next;
                    });
                  }}
                />
                <Input
                  label="PAT % *"
                  name="pat_percent"
                  inputMode="decimal"
                  value={financing.pat_percent}
                  error={financingFieldErrors.pat_percent}
                  hint={`Profit after tax, ${PAT_PERCENT_MIN} to ${PAT_PERCENT_MAX}, up to 2 decimals`}
                  onChange={(e) => {
                    setFinancing((prev) => ({
                      ...prev,
                      pat_percent: sanitizePatPercentInput(
                        e.target.value,
                        prev.pat_percent,
                      ),
                    }));
                    setFinancingFieldErrors((prev) => {
                      const next = { ...prev };
                      delete next.pat_percent;
                      return next;
                    });
                  }}
                  onBlur={() => {
                    setFinancing((prev) => ({
                      ...prev,
                      pat_percent: finalizePatPercentInput(prev.pat_percent),
                    }));
                  }}
                />
                <Input
                  label="Business vintage (years) *"
                  name="business_vintage_years"
                  inputMode="decimal"
                  value={financing.business_vintage_years}
                  error={financingFieldErrors.business_vintage_years}
                  hint="How long the business has been operating"
                  onChange={(e) => {
                    setFinancing((prev) => ({
                      ...prev,
                      business_vintage_years: sanitizeVintageYearsInput(
                        e.target.value,
                        prev.business_vintage_years,
                      ),
                    }));
                    setFinancingFieldErrors((prev) => {
                      const next = { ...prev };
                      delete next.business_vintage_years;
                      return next;
                    });
                  }}
                  onBlur={() => {
                    setFinancing((prev) => ({
                      ...prev,
                      business_vintage_years: finalizeVintageYearsInput(
                        prev.business_vintage_years,
                      ),
                    }));
                  }}
                />
                <Select
                  label="Does the merchant file VAT? *"
                  name="files_vat"
                  value={financing.files_vat}
                  error={financingFieldErrors.files_vat}
                  onChange={(e) => {
                    const filesVat = e.target.value;
                    setFinancing((prev) => ({
                      ...prev,
                      files_vat: filesVat,
                      vat_number: filesVat === "true" ? prev.vat_number : "",
                    }));
                    setFinancingFieldErrors((prev) => {
                      const next = { ...prev };
                      delete next.files_vat;
                      if (filesVat !== "true") delete next.vat_number;
                      return next;
                    });
                  }}
                  options={[
                    { value: "", label: "Select Yes or No" },
                    { value: "true", label: "Yes" },
                    { value: "false", label: "No" },
                  ]}
                />
                {financing.files_vat === "true" ? (
                  <Input
                    label="VAT number *"
                    name="vat_number"
                    inputMode="numeric"
                    maxLength={VAT_NUMBER_LENGTH}
                    value={financing.vat_number}
                    error={financingFieldErrors.vat_number}
                    hint={`${VAT_NUMBER_LENGTH}-digit UAE TRN`}
                    onChange={(e) => {
                      setFinancing((prev) => ({
                        ...prev,
                        vat_number: e.target.value.replace(/[^0-9]/g, "").slice(0, VAT_NUMBER_LENGTH),
                      }));
                      setFinancingFieldErrors((prev) => {
                        const next = { ...prev };
                        delete next.vat_number;
                        return next;
                      });
                    }}
                  />
                ) : null}
                {financing.platform === "other" ? (
                  <Input
                    label={platformNameRequired ? "Platform name *" : "Platform name"}
                    name="platform_name"
                    value={financing.platform_name}
                    error={financingFieldErrors.platform_name}
                    hint="Required when Sales Platform is Other"
                    onChange={(e) => {
                      setFinancing((prev) => ({ ...prev, platform_name: e.target.value }));
                      setFinancingFieldErrors((prev) => {
                        const next = { ...prev };
                        delete next.platform_name;
                        return next;
                      });
                    }}
                  />
                ) : null}
                <div className="sm:col-span-2 lg:col-span-3">
                  <Textarea
                    label="Brief about the company, promoter and business *"
                    name="company_business_brief"
                    value={financing.company_business_brief}
                    error={financingFieldErrors.company_business_brief}
                    maxLength={COMPANY_BUSINESS_BRIEF_MAX_LENGTH}
                    hint={`Describe the company, promoters, and how the business operates. ${financing.company_business_brief.length}/${COMPANY_BUSINESS_BRIEF_MAX_LENGTH} characters.`}
                    rows={5}
                    onChange={(e) => {
                      setFinancing((prev) => ({
                        ...prev,
                        company_business_brief: e.target.value.slice(
                          0,
                          COMPANY_BUSINESS_BRIEF_MAX_LENGTH,
                        ),
                      }));
                      setFinancingFieldErrors((prev) => {
                        const next = { ...prev };
                        delete next.company_business_brief;
                        return next;
                      });
                    }}
                  />
                </div>
              </div>

              <LendingLinesSection
                lines={financing.existing_lending_lines}
                noExistingLoans={financing.no_existing_loans}
                errors={financingFieldErrors}
                onNoExistingLoansChange={(no_existing_loans) => {
                  setFinancing((prev) => ({ ...prev, no_existing_loans }));
                  clearLendingLineErrors();
                }}
                onChange={(existing_lending_lines) => {
                  setFinancing((prev) => ({ ...prev, existing_lending_lines }));
                  clearLendingLineErrors();
                }}
              />

              <DocumentsSections
                locked={locked}
                docsReadOnly={docsReadOnly}
                bankStatementsComplete={bankStatementsComplete}
                otherDocsDescription={otherDocsDescription}
                otherDocsIncomplete={otherDocsIncomplete}
                showBankForm={showBankForm}
                setShowBankForm={setShowBankForm}
                bankName={bankName}
                setBankName={setBankName}
                accountNumber={accountNumber}
                setAccountNumber={setAccountNumber}
                bankFieldErrors={bankFieldErrors}
                bankSaving={bankSaving}
                onAddBank={onAddBank}
                bankDocs={bankDocs}
                bankDescription={PARTNER_FINANCIAL_DOC_COPY[DOC_KEY_BANK_STATEMENTS]}
                showMarketForm={showMarketForm}
                setShowMarketForm={setShowMarketForm}
                marketplace={marketplace}
                setMarketplace={setMarketplace}
                country={country}
                setCountry={setCountry}
                marketFieldErrors={marketFieldErrors}
                marketSaving={marketSaving}
                onAddMarketplace={onAddMarketplace}
                marketplaceDocs={marketplaceDocs}
                invoiceDescription={PARTNER_FINANCIAL_DOC_COPY[DOC_KEY_INVOICES]}
                showBuyerForm={showBuyerForm}
                setShowBuyerForm={setShowBuyerForm}
                buyerName={buyerName}
                setBuyerName={setBuyerName}
                buyerFieldErrors={buyerFieldErrors}
                buyerSaving={buyerSaving}
                onAddBuyer={onAddBuyer}
                buyerInvoiceDocs={buyerInvoiceDocs}
                buyerInvoicesRequired={buyerInvoicesRequired}
                buyerInvoicesComplete={buyerInvoicesComplete}
                showFinancialYearForm={showFinancialYearForm}
                setShowFinancialYearForm={setShowFinancialYearForm}
                financialYear={financialYear}
                setFinancialYear={setFinancialYear}
                financialYearError={financialYearError}
                financialYearSaving={financialYearSaving}
                onAddFinancialYear={onAddFinancialYear}
                auditedFinancialsDocs={auditedFinancialsDocs}
                availableFinancialYears={availableFinancialYears}
                minFinancialYears={minFinancialYears}
                financialsRequired={financialsRequired}
                financialsMandatoryAbove={financialsMandatoryAbove}
                financialsComplete={financialsComplete}
                uploadingUuid={uploadingUuid}
                deletingKey={deletingKey}
                openUpload={openUpload}
                onDeleteContainer={onDeleteContainer}
                onDeleteFile={onDeleteFile}
                applicationUuid={application.uuid}
                flatChecklist={flatChecklist}
                refreshDocuments={refreshDocuments}
              />

              <div className="flex justify-end gap-2 border-t border-line pt-6">
                {hasSectionData ? (
                  <Button type="button" variant="ghost" size="sm" onClick={exitEditing}>
                    Cancel
                  </Button>
                ) : null}
                <Button type="submit" size="sm" loading={financingSaving}>
                  Save financing details
                </Button>
              </div>
            </form>
          ) : (
            <div className="space-y-8">
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                <InfoCard
                  label="Type of financing"
                  value={
                    FINANCING_TYPE_LABELS[financing.type_of_financing] ||
                    financing.type_of_financing ||
                    "—"
                  }
                />
                <InfoCard
                  label="Sales platform"
                  value={
                    PLATFORM_LABELS[financing.platform] || financing.platform || "—"
                  }
                />
                <InfoCard
                  label="Loan amount requested (AED)"
                  value={
                    financing.loan_amount_requested
                      ? Number(financing.loan_amount_requested).toLocaleString()
                      : "—"
                  }
                />
                <InfoCard
                  label="Last 12 month revenue (AED)"
                  value={
                    financing.last_12_month_revenue
                      ? Number(financing.last_12_month_revenue).toLocaleString()
                      : "—"
                  }
                />
                <InfoCard
                  label="PAT %"
                  value={financing.pat_percent ? `${financing.pat_percent}%` : "—"}
                />
                <InfoCard
                  label="Business vintage"
                  value={
                    financing.business_vintage_years
                      ? `${financing.business_vintage_years} years`
                      : "—"
                  }
                />
                <InfoCard
                  label="Merchant files VAT"
                  value={
                    financing.files_vat === "true"
                      ? "Yes"
                      : financing.files_vat === "false"
                        ? "No"
                        : "—"
                  }
                />
                {financing.files_vat === "true" ? (
                  <InfoCard label="VAT number" value={financing.vat_number || "—"} />
                ) : null}
                {financing.platform === "other" || financing.platform_name ? (
                  <InfoCard label="Platform name" value={financing.platform_name || "—"} />
                ) : null}
                <div className="sm:col-span-2 lg:col-span-3">
                  <InfoCard
                    label="Brief about the company, promoter and business"
                    value={financing.company_business_brief || "—"}
                  />
                </div>
              </div>

              <LendingLinesSection
                lines={financing.existing_lending_lines}
                noExistingLoans={financing.no_existing_loans}
                readOnly
              />

              <DocumentsSections
                locked={locked}
                docsReadOnly={docsReadOnly}
                bankStatementsComplete={bankStatementsComplete}
                otherDocsDescription={otherDocsDescription}
                otherDocsIncomplete={otherDocsIncomplete}
                showBankForm={showBankForm}
                setShowBankForm={setShowBankForm}
                bankName={bankName}
                setBankName={setBankName}
                accountNumber={accountNumber}
                setAccountNumber={setAccountNumber}
                bankFieldErrors={bankFieldErrors}
                bankSaving={bankSaving}
                onAddBank={onAddBank}
                bankDocs={bankDocs}
                bankDescription={PARTNER_FINANCIAL_DOC_COPY[DOC_KEY_BANK_STATEMENTS]}
                showMarketForm={showMarketForm}
                setShowMarketForm={setShowMarketForm}
                marketplace={marketplace}
                setMarketplace={setMarketplace}
                country={country}
                setCountry={setCountry}
                marketFieldErrors={marketFieldErrors}
                marketSaving={marketSaving}
                onAddMarketplace={onAddMarketplace}
                marketplaceDocs={marketplaceDocs}
                invoiceDescription={PARTNER_FINANCIAL_DOC_COPY[DOC_KEY_INVOICES]}
                showBuyerForm={showBuyerForm}
                setShowBuyerForm={setShowBuyerForm}
                buyerName={buyerName}
                setBuyerName={setBuyerName}
                buyerFieldErrors={buyerFieldErrors}
                buyerSaving={buyerSaving}
                onAddBuyer={onAddBuyer}
                buyerInvoiceDocs={buyerInvoiceDocs}
                buyerInvoicesRequired={buyerInvoicesRequired}
                buyerInvoicesComplete={buyerInvoicesComplete}
                showFinancialYearForm={showFinancialYearForm}
                setShowFinancialYearForm={setShowFinancialYearForm}
                financialYear={financialYear}
                setFinancialYear={setFinancialYear}
                financialYearError={financialYearError}
                financialYearSaving={financialYearSaving}
                onAddFinancialYear={onAddFinancialYear}
                auditedFinancialsDocs={auditedFinancialsDocs}
                availableFinancialYears={availableFinancialYears}
                minFinancialYears={minFinancialYears}
                financialsRequired={financialsRequired}
                financialsMandatoryAbove={financialsMandatoryAbove}
                financialsComplete={financialsComplete}
                uploadingUuid={uploadingUuid}
                deletingKey={deletingKey}
                openUpload={openUpload}
                onDeleteContainer={onDeleteContainer}
                onDeleteFile={onDeleteFile}
                applicationUuid={application.uuid}
                flatChecklist={flatChecklist}
                refreshDocuments={refreshDocuments}
              />
            </div>
          )}
        </section>
      </div>
    </>
  );
}

function DocumentsSections({
  locked,
  docsReadOnly,
  bankStatementsComplete,
  otherDocsDescription,
  otherDocsIncomplete,
  showBankForm,
  setShowBankForm,
  bankName,
  setBankName,
  accountNumber,
  setAccountNumber,
  bankFieldErrors,
  bankSaving,
  onAddBank,
  bankDocs,
  bankDescription,
  showMarketForm,
  setShowMarketForm,
  marketplace,
  setMarketplace,
  country,
  setCountry,
  marketFieldErrors,
  marketSaving,
  onAddMarketplace,
  marketplaceDocs,
  invoiceDescription,
  showBuyerForm,
  setShowBuyerForm,
  buyerName,
  setBuyerName,
  buyerFieldErrors,
  buyerSaving,
  onAddBuyer,
  buyerInvoiceDocs,
  buyerInvoicesRequired,
  buyerInvoicesComplete,
  showFinancialYearForm,
  setShowFinancialYearForm,
  financialYear,
  setFinancialYear,
  financialYearError,
  financialYearSaving,
  onAddFinancialYear,
  auditedFinancialsDocs,
  availableFinancialYears,
  minFinancialYears,
  financialsRequired,
  financialsMandatoryAbove,
  financialsComplete,
  uploadingUuid,
  deletingKey,
  openUpload,
  onDeleteContainer,
  onDeleteFile,
  applicationUuid,
  flatChecklist,
  refreshDocuments,
}: {
  locked: boolean;
  docsReadOnly: boolean;
  bankStatementsComplete: boolean;
  otherDocsDescription: string | undefined;
  otherDocsIncomplete: string[];
  showBankForm: boolean;
  setShowBankForm: (v: boolean) => void;
  bankName: string;
  setBankName: (v: string) => void;
  accountNumber: string;
  setAccountNumber: (v: string) => void;
  bankFieldErrors: Record<string, string>;
  bankSaving: boolean;
  onAddBank: () => Promise<void>;
  bankDocs: ApplicationDocument[];
  bankDescription?: string | null;
  showMarketForm: boolean;
  setShowMarketForm: (v: boolean) => void;
  marketplace: string;
  setMarketplace: (v: string) => void;
  country: string;
  setCountry: (v: string) => void;
  marketFieldErrors: Record<string, string>;
  marketSaving: boolean;
  onAddMarketplace: () => Promise<void>;
  marketplaceDocs: ApplicationDocument[];
  invoiceDescription?: string | null;
  showBuyerForm: boolean;
  setShowBuyerForm: (v: boolean) => void;
  buyerName: string;
  setBuyerName: (v: string) => void;
  buyerFieldErrors: Record<string, string>;
  buyerSaving: boolean;
  onAddBuyer: () => Promise<void>;
  buyerInvoiceDocs: ApplicationDocument[];
  buyerInvoicesRequired: boolean;
  buyerInvoicesComplete: boolean;
  showFinancialYearForm: boolean;
  setShowFinancialYearForm: (v: boolean) => void;
  financialYear: string;
  setFinancialYear: (v: string) => void;
  financialYearError: string | null;
  financialYearSaving: boolean;
  onAddFinancialYear: () => Promise<void>;
  auditedFinancialsDocs: ApplicationDocument[];
  availableFinancialYears: string[];
  minFinancialYears: number;
  financialsRequired: boolean;
  financialsMandatoryAbove: number | null;
  financialsComplete: boolean;
  uploadingUuid: string | null;
  deletingKey: string | null;
  openUpload: (documentUuid: string) => void;
  onDeleteContainer: (documentUuid: string, label: string) => Promise<void>;
  onDeleteFile: (documentUuid: string, fileUuid: string) => Promise<void>;
  applicationUuid: string;
  flatChecklist: DocumentChecklistItem[];
  refreshDocuments: () => Promise<void>;
}) {
  return (
    <>
      <div className="border-t border-line pt-6">
        <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
          <div>
            <h3 className="text-base font-semibold text-ink">
              Bank statements <span className="text-danger">*</span>
            </h3>
            {bankDescription ? (
              <p className="mt-1 text-sm text-muted">{bankDescription}</p>
            ) : null}
            {locked || !docsReadOnly ? (
              <p className="mt-1 text-sm text-muted">
                {locked
                  ? "Bank statements are locked because this loan is verified."
                  : "Mandatory: add at least one bank account and upload at least one statement file."}
              </p>
            ) : null}
            {!docsReadOnly && !bankStatementsComplete ? (
              <p className="mt-2 text-xs font-medium text-danger">
                Incomplete — need a bank account with at least one uploaded statement.
              </p>
            ) : null}
            {(docsReadOnly || bankStatementsComplete) && bankStatementsComplete ? (
              <p className="mt-2 text-xs font-medium text-brand">Bank statements complete.</p>
            ) : null}
          </div>
          {docsReadOnly ? null : (
            <Button
              type="button"
              size="sm"
              variant="secondary"
              onClick={() => setShowBankForm(true)}
            >
              + Add bank account
            </Button>
          )}
        </div>

        {!docsReadOnly && showBankForm ? (
          <div className="mb-5 rounded-xl border border-line bg-surface p-4">
            <div className="grid gap-3 sm:grid-cols-2">
              <Input
                label="Bank name *"
                name="bank_name"
                value={bankName}
                error={bankFieldErrors.bank_name}
                onChange={(e) => setBankName(e.target.value)}
              />
              <Input
                label="Account number *"
                name="account_number"
                value={accountNumber}
                error={bankFieldErrors.account_number}
                onChange={(e) => setAccountNumber(e.target.value)}
              />
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setShowBankForm(false)}
              >
                Cancel
              </Button>
              <Button
                type="button"
                size="sm"
                loading={bankSaving}
                onClick={() => void onAddBank()}
              >
                Save bank
              </Button>
            </div>
          </div>
        ) : null}

        {bankDocs.length === 0 ? (
          <p className="rounded-xl border border-dashed border-line px-4 py-8 text-center text-sm text-muted">
            No bank accounts added yet.
          </p>
        ) : (
          <div className="space-y-4">
            {bankDocs.map((doc, index) => (
              <ContainerCard
                key={doc.uuid}
                index={index + 1}
                title={metaString(doc.metadata, "bank_name") || "Bank account"}
                subtitle={`Account: ${metaString(doc.metadata, "account_number")}`}
                files={doc.document_files || []}
                readOnly={docsReadOnly}
                uploading={uploadingUuid === doc.uuid}
                deletingContainer={deletingKey === doc.uuid}
                deletingFileUuid={deletingKey}
                onUpload={() => openUpload(doc.uuid)}
                onDeleteContainer={() =>
                  void onDeleteContainer(doc.uuid, "this bank account")
                }
                onDeleteFile={(fileUuid) => void onDeleteFile(doc.uuid, fileUuid)}
              />
            ))}
          </div>
        )}
      </div>

      <div className="border-t border-line pt-6">
        <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
          <div>
            <h3 className="text-base font-semibold text-ink">
              Audited financials{" "}
              {financialsRequired ? <span className="text-danger">*</span> : null}
            </h3>
            <p className="mt-1 text-sm text-muted">{AUDITED_FINANCIALS_DESCRIPTION}</p>
            {locked || !docsReadOnly ? (
              <p className="mt-1 text-sm text-muted">
                {locked
                  ? "Audited financials are locked because this loan is verified."
                  : financialsRequired
                    ? `Mandatory: select a financial year and upload the statements for it. At least ${minFinancialYears} financial years are required because the requested amount is over ${financialsMandatoryAbove?.toLocaleString()} AED.`
                    : `Optional. Audited financials become mandatory (at least ${minFinancialYears} financial years) when the requested amount is over ${financialsMandatoryAbove?.toLocaleString()} AED.`}
              </p>
            ) : null}
            {!docsReadOnly && financialsRequired && !financialsComplete ? (
              <p className="mt-2 text-xs font-medium text-danger">
                Incomplete — upload statements for at least {minFinancialYears} financial years.
              </p>
            ) : null}
            {financialsRequired && financialsComplete ? (
              <p className="mt-2 text-xs font-medium text-brand">Audited financials complete.</p>
            ) : null}
          </div>
          {docsReadOnly ? null : (
            <Button
              type="button"
              size="sm"
              variant="secondary"
              disabled={availableFinancialYears.length === 0}
              onClick={() => setShowFinancialYearForm(true)}
            >
              + Add financial year
            </Button>
          )}
        </div>

        {!docsReadOnly && showFinancialYearForm ? (
          <div className="mb-5 rounded-xl border border-line bg-surface p-4">
            <div className="grid gap-3 sm:grid-cols-2">
              <Select
                label="Financial year *"
                name="financial_year"
                value={financialYear}
                error={financialYearError ?? undefined}
                onChange={(e) => setFinancialYear(e.target.value)}
                options={[
                  { value: "", label: "Select financial year" },
                  ...availableFinancialYears.map((year) => ({ value: year, label: year })),
                ]}
              />
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setShowFinancialYearForm(false)}
              >
                Cancel
              </Button>
              <Button
                type="button"
                size="sm"
                loading={financialYearSaving}
                onClick={() => void onAddFinancialYear()}
              >
                Save financial year
              </Button>
            </div>
          </div>
        ) : null}

        {auditedFinancialsDocs.length === 0 ? (
          <p className="rounded-xl border border-dashed border-line px-4 py-8 text-center text-sm text-muted">
            No financial years added yet.
          </p>
        ) : (
          <div className="space-y-4">
            {auditedFinancialsDocs.map((doc, index) => (
              <ContainerCard
                key={doc.uuid}
                index={index + 1}
                title={financialYearOfDocument(doc) || "Financial year"}
                subtitle=""
                files={doc.document_files || []}
                readOnly={docsReadOnly}
                uploading={uploadingUuid === doc.uuid}
                deletingContainer={deletingKey === doc.uuid}
                deletingFileUuid={deletingKey}
                onUpload={() => openUpload(doc.uuid)}
                onDeleteContainer={() =>
                  void onDeleteContainer(doc.uuid, "this financial year")
                }
                onDeleteFile={(fileUuid) => void onDeleteFile(doc.uuid, fileUuid)}
              />
            ))}
          </div>
        )}
      </div>

      <div className="border-t border-line pt-6">
        <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
          <div>
            <h3 className="text-base font-semibold text-ink">
              Payment gateway / marketplace sales data
            </h3>
            {invoiceDescription ? (
              <p className="mt-1 text-sm text-muted">{invoiceDescription}</p>
            ) : null}
            {locked || !docsReadOnly ? (
              <p className="mt-1 text-sm text-muted">
                {locked
                  ? "Marketplace sales data is locked because this loan is verified."
                  : "Optional. If you add a marketplace, name and country are both required."}
              </p>
            ) : null}
          </div>
          {docsReadOnly ? null : (
            <Button
              type="button"
              size="sm"
              variant="secondary"
              onClick={() => setShowMarketForm(true)}
            >
              + Add marketplace
            </Button>
          )}
        </div>

        {!docsReadOnly && showMarketForm ? (
          <div className="mb-5 rounded-xl border border-line bg-surface p-4">
            <div className="grid gap-3 sm:grid-cols-2">
              <Input
                label="Marketplace name *"
                name="marketplace"
                value={marketplace}
                error={marketFieldErrors.marketplace}
                onChange={(e) => setMarketplace(e.target.value)}
              />
              <Input
                label="Country *"
                name="country"
                value={country}
                error={marketFieldErrors.country}
                onChange={(e) => setCountry(e.target.value)}
              />
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setShowMarketForm(false)}
              >
                Cancel
              </Button>
              <Button
                type="button"
                size="sm"
                loading={marketSaving}
                onClick={() => void onAddMarketplace()}
              >
                Save marketplace
              </Button>
            </div>
          </div>
        ) : null}

        {marketplaceDocs.length === 0 ? (
          <p className="rounded-xl border border-dashed border-line px-4 py-8 text-center text-sm text-muted">
            No marketplaces added yet.
          </p>
        ) : (
          <div className="space-y-4">
            {marketplaceDocs.map((doc, index) => (
              <ContainerCard
                key={doc.uuid}
                index={index + 1}
                title={metaString(doc.metadata, "marketplace") || "Marketplace"}
                subtitle={`Country: ${metaString(doc.metadata, "country")}`}
                files={doc.document_files || []}
                readOnly={docsReadOnly}
                uploading={uploadingUuid === doc.uuid}
                deletingContainer={deletingKey === doc.uuid}
                deletingFileUuid={deletingKey}
                onUpload={() => openUpload(doc.uuid)}
                onDeleteContainer={() =>
                  void onDeleteContainer(doc.uuid, "this marketplace")
                }
                onDeleteFile={(fileUuid) => void onDeleteFile(doc.uuid, fileUuid)}
              />
            ))}
          </div>
        )}
      </div>

      <div className="border-t border-line pt-6">
        <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
          <div>
            <h3 className="text-base font-semibold text-ink">
              Invoice copies of customers{" "}
              {buyerInvoicesRequired ? <span className="text-danger">*</span> : null}
            </h3>
            <p className="mt-1 text-sm text-muted">
              Please upload sample invoice copies of your buyers against whose invoices you
              need the facility.
            </p>
            {locked || !docsReadOnly ? (
              <p className="mt-1 text-sm text-muted">
                {locked
                  ? "Invoice copies are locked because this loan is verified."
                  : buyerInvoicesRequired
                    ? "Mandatory: add at least one buyer and upload their invoice copies. This is required for Invoice Discounting."
                    : "Optional unless Invoice Discounting is selected as the type of financing."}
              </p>
            ) : null}
            {!docsReadOnly && buyerInvoicesRequired && !buyerInvoicesComplete ? (
              <p className="mt-2 text-xs font-medium text-danger">
                Incomplete — need a buyer with at least one uploaded invoice copy.
              </p>
            ) : null}
            {buyerInvoicesRequired && buyerInvoicesComplete ? (
              <p className="mt-2 text-xs font-medium text-brand">Invoice copies complete.</p>
            ) : null}
          </div>
          {docsReadOnly ? null : (
            <Button
              type="button"
              size="sm"
              variant="secondary"
              onClick={() => setShowBuyerForm(true)}
            >
              + Add buyer
            </Button>
          )}
        </div>

        {!docsReadOnly && showBuyerForm ? (
          <div className="mb-5 rounded-xl border border-line bg-surface p-4">
            <div className="grid gap-3 sm:grid-cols-2">
              <Input
                label="Buyer / customer name *"
                name="buyer_name"
                value={buyerName}
                error={buyerFieldErrors.buyer_name}
                onChange={(e) => setBuyerName(e.target.value)}
              />
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setShowBuyerForm(false)}
              >
                Cancel
              </Button>
              <Button
                type="button"
                size="sm"
                loading={buyerSaving}
                onClick={() => void onAddBuyer()}
              >
                Save buyer
              </Button>
            </div>
          </div>
        ) : null}

        {buyerInvoiceDocs.length === 0 ? (
          <p className="rounded-xl border border-dashed border-line px-4 py-8 text-center text-sm text-muted">
            No buyers added yet.
          </p>
        ) : (
          <div className="space-y-4">
            {buyerInvoiceDocs.map((doc, index) => (
              <ContainerCard
                key={doc.uuid}
                index={index + 1}
                title={metaString(doc.metadata, "buyer_name") || "Buyer"}
                subtitle=""
                files={doc.document_files || []}
                readOnly={docsReadOnly}
                uploading={uploadingUuid === doc.uuid}
                deletingContainer={deletingKey === doc.uuid}
                deletingFileUuid={deletingKey}
                onUpload={() => openUpload(doc.uuid)}
                onDeleteContainer={() => void onDeleteContainer(doc.uuid, "this buyer")}
                onDeleteFile={(fileUuid) => void onDeleteFile(doc.uuid, fileUuid)}
              />
            ))}
          </div>
        )}
      </div>

      <div className="border-t border-line pt-6">
        <DocumentUploadPanel
          applicationUuid={applicationUuid}
          checklist={flatChecklist}
          readOnly={docsReadOnly}
          embedded
          title="Other financial documents"
          description={otherDocsDescription}
          emptyMessage="No other financial document types configured."
          onUploaded={refreshDocuments}
        />
        {!docsReadOnly
          ? otherDocsIncomplete.map((message) => (
            <p key={message} className="mt-2 text-xs font-medium text-danger">
              {message}
            </p>
          ))
          : null}
      </div>
    </>
  );
}

function LendingLinesSection({
  lines,
  noExistingLoans,
  errors = {},
  readOnly = false,
  onChange,
  onNoExistingLoansChange,
}: {
  lines: LendingLineFormValues[];
  noExistingLoans: boolean;
  errors?: Record<string, string>;
  readOnly?: boolean;
  onChange?: (lines: LendingLineFormValues[]) => void;
  onNoExistingLoansChange?: (noExistingLoans: boolean) => void;
}) {
  const updateLine = (index: number, patch: Partial<LendingLineFormValues>) => {
    onChange?.(lines.map((line, i) => (i === index ? { ...line, ...patch } : line)));
  };

  return (
    <div className="border-t border-line pt-6">
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold text-ink">
            Existing lending lines{" "}
            {readOnly ? null : <span className="text-danger">*</span>}
          </h3>
          <p className="mt-1 text-sm text-muted">
            Add the merchant&apos;s current lender facilities, or mark that there are none.
          </p>
        </div>
        {readOnly ? null : (
          <div className="flex flex-wrap items-center gap-4">
            <label className="flex items-center gap-2 text-sm text-ink">
              <input
                type="checkbox"
                className="size-4 rounded border-line"
                checked={noExistingLoans}
                onChange={(e) => onNoExistingLoansChange?.(e.target.checked)}
              />
              No existing lending lines
            </label>
            {noExistingLoans ? null : (
              <Button
                type="button"
                size="sm"
                variant="secondary"
                onClick={() => onChange?.([...lines, emptyLendingLine()])}
              >
                Add lending line
              </Button>
            )}
          </div>
        )}
      </div>

      {errors.existing_lending_lines ? (
        <p className="mb-3 text-xs font-medium text-danger">
          {errors.existing_lending_lines}
        </p>
      ) : null}

      {noExistingLoans ? (
        <p className="rounded-xl border border-dashed border-line px-4 py-8 text-center text-sm text-muted">
          Merchant has no existing lending lines.
        </p>
      ) : lines.length === 0 ? (
        <p className="rounded-xl border border-dashed border-line px-4 py-8 text-center text-sm text-muted">
          No existing lending lines added.
        </p>
      ) : (
        <div className="space-y-4">
          {lines.map((line, index) => (
            <div key={index} className="rounded-xl border border-line bg-surface p-4">
              <div className="mb-3 flex items-center justify-between gap-3">
                <p className="text-sm font-medium text-ink">Lending line {index + 1}</p>
                {readOnly ? null : (
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => onChange?.(lines.filter((_, i) => i !== index))}
                  >
                    Remove
                  </Button>
                )}
              </div>
              {readOnly ? (
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  <InfoCard label="Lender name" value={line.lender_name} />
                  <InfoCard
                    label="Loan amount (AED)"
                    value={
                      line.loan_amount ? Number(line.loan_amount).toLocaleString() : "—"
                    }
                  />
                  <InfoCard label="Facility type" value={line.facility_type} />
                  <InfoCard
                    label="Outstanding (AED)"
                    value={
                      line.outstanding
                        ? Number(line.outstanding).toLocaleString()
                        : "—"
                    }
                  />
                  <InfoCard
                    label="EMI (AED)"
                    value={line.emi ? Number(line.emi).toLocaleString() : "—"}
                  />
                  <InfoCard label="Remarks" value={line.remarks} />
                </div>
              ) : (
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  <Input
                    label="Lender name *"
                    name={`lending_line_${index}_lender_name`}
                    value={line.lender_name}
                    error={errors[`lending_line_${index}_lender_name`]}
                    onChange={(e) => updateLine(index, { lender_name: e.target.value })}
                  />
                  <Input
                    label="Loan amount (AED) *"
                    name={`lending_line_${index}_loan_amount`}
                    inputMode="numeric"
                    value={line.loan_amount}
                    error={errors[`lending_line_${index}_loan_amount`]}
                    onChange={(e) =>
                      updateLine(index, {
                        loan_amount: e.target.value.replace(/[^0-9]/g, ""),
                      })
                    }
                  />
                  <Input
                    label="Facility type *"
                    name={`lending_line_${index}_facility_type`}
                    value={line.facility_type}
                    error={errors[`lending_line_${index}_facility_type`]}
                    onChange={(e) =>
                      updateLine(index, { facility_type: e.target.value })
                    }
                  />
                  <Input
                    label="Outstanding (AED)"
                    name={`lending_line_${index}_outstanding`}
                    inputMode="numeric"
                    value={line.outstanding}
                    error={errors[`lending_line_${index}_outstanding`]}
                    onChange={(e) =>
                      updateLine(index, {
                        outstanding: e.target.value.replace(/[^0-9]/g, ""),
                      })
                    }
                  />
                  <Input
                    label="EMI (AED)"
                    name={`lending_line_${index}_emi`}
                    inputMode="numeric"
                    value={line.emi}
                    error={errors[`lending_line_${index}_emi`]}
                    onChange={(e) =>
                      updateLine(index, { emi: e.target.value.replace(/[^0-9]/g, "") })
                    }
                  />
                  <Input
                    label="Remarks"
                    name={`lending_line_${index}_remarks`}
                    value={line.remarks}
                    error={errors[`lending_line_${index}_remarks`]}
                    onChange={(e) => updateLine(index, { remarks: e.target.value })}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ContainerCard({
  index,
  title,
  subtitle,
  files,
  readOnly = false,
  uploading,
  deletingContainer,
  deletingFileUuid,
  onUpload,
  onDeleteContainer,
  onDeleteFile,
}: {
  index: number;
  title: string;
  subtitle: string;
  files: ApplicationDocument["document_files"];
  readOnly?: boolean;
  uploading: boolean;
  deletingContainer: boolean;
  deletingFileUuid: string | null;
  onUpload: () => void;
  onDeleteContainer: () => void;
  onDeleteFile: (fileUuid: string) => void;
}) {
  return (
    <div className="rounded-xl border border-line bg-surface p-4">
      <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-brand/10 text-sm font-semibold text-brand">
            {index}
          </span>
          <div>
            <p className="font-medium text-ink">{title}</p>
            <p className="text-xs text-muted">{subtitle}</p>
          </div>
        </div>
        {readOnly ? null : (
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              size="sm"
              variant={files.length ? "secondary" : "primary"}
              loading={uploading}
              onClick={onUpload}
            >
              {files.length ? "Add files" : "Upload"}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              loading={deletingContainer}
              onClick={onDeleteContainer}
            >
              Delete
            </Button>
          </div>
        )}
      </div>

      {files.length === 0 ? (
        <p className="text-sm text-muted">No files uploaded yet.</p>
      ) : (
        <ul className="space-y-2 rounded-xl border border-line bg-surface-raised p-3">
          {files.map((file) => (
            <li
              key={file.uuid}
              className="flex flex-wrap items-center justify-between gap-2 text-sm"
            >
              <div className="min-w-0 flex-1">
                {file.file_url ? (
                  <a
                    href={file.file_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="break-all font-medium text-brand hover:underline"
                  >
                    {file.file_name}
                  </a>
                ) : (
                  <span className="break-all text-ink">{file.file_name}</span>
                )}
              </div>
              {readOnly ? null : (
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  loading={deletingFileUuid === file.uuid}
                  onClick={() => onDeleteFile(file.uuid)}
                >
                  Delete
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
